import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconChevronRight, IconTrash } from "@tabler/icons-react";
import {
  reloadCurrentUser,
  type CurrentUser,
} from "../../../../domains/user/hooks/useCurrentUser";
import { useLogOutUser } from "../../../../domains/user/hooks/useLogOutUser";
import { resetAvatarPicker } from "../../../../domains/user/stores/avatarPickerStore";
import { getItemTranslation } from "../../../../domains/app/utils/getItemTranslation";
import {
  ACCOUNT_PAGE,
  closeDesktopSettings,
  openDesktopSettings,
  leaveDesktopSettingsPage,
  type AccountField,
} from "../../../../domains/app/stores/desktopSettingsStore";
import { apiClient, useApiQuery } from "../../../../services/api";
import { useConversationEngine } from "../../../../domains/assistant/useConversationEngine";
import type { ConversationEngine } from "../../../../domains/assistant/conversationEngine";
import { useDeleteConversations } from "../../../../domains/gpt/hooks/useManageConversation";
import { cn } from "../../../../lib/utils";
import { gatEventUserDeleteAccount } from "../../../../tools/analytics/domains/user.analytics";
import { displayToast } from "../../../../components/app/AppToast";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { Textarea } from "../../../../components/ui/Textarea";
import {
  formatProfileLink,
  isValidProfileLink,
} from "../../../../domains/user/profileLink";
import ProfilePicture from "../../../../components/ProfilePicture";
import {
  SettingsPage,
  SettingsRow,
  SettingsSection,
} from "../components/SettingsPanel";

const ROW_BUTTON_CLASS = "min-w-37 shrink-0";

/**
 * Account (Figma "D3.settings.account"): who the reader is, each field edited
 * in its own row (Figma "account.change-name", "…edit-bio", "…edit-link"),
 * then the deletions pinned to the bottom of the panel.
 *
 * The email is read-only because there is no change-email flow.
 */
export function AccountSection({
  currentUser,
  editing,
}: {
  currentUser: CurrentUser;
  /** The row open for editing, if any. */
  editing: AccountField | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useConversationHistory();

  const isVerified = currentUser.lemonCertified === true;
  const profession =
    currentUser.professionModel === null
      ? null
      : getItemTranslation(currentUser.professionModel);
  const identityLine = [profession, isVerified ? t("Verified HCP") : null]
    .filter((part) => part !== null)
    .join(" · ");

  const fields: readonly {
    field: AccountField;
    title: string;
    value: string;
    placeholder: string;
  }[] = [
    {
      field: "firstName",
      title: t("First name"),
      value: currentUser.firstname === null ? "" : currentUser.firstname,
      placeholder: t("First name"),
    },
    {
      field: "lastName",
      title: t("Last name"),
      value: currentUser.lastname === null ? "" : currentUser.lastname,
      placeholder: t("Last name"),
    },
    {
      field: "bio",
      title: t("Bio"),
      value: currentUser.bio === null ? "" : currentUser.bio,
      placeholder: t("Add a biography to your profile"),
    },
    {
      field: "link",
      title: t("Link"),
      value: currentUser.link === null ? "" : currentUser.link,
      placeholder: t("Add a link to your profile"),
    },
  ];

  return (
    <SettingsPage back={null}>
      <SettingsSection title={t("Account")} description={null}>
        <div className="flex items-center gap-3.5 py-2.5">
          <ProfilePicture user={currentUser} height={40} badge={null} />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-sm font-semibold text-ds-slate-950">
              {currentUser.fullname === null ? "" : currentUser.fullname}
            </span>
            {identityLine === "" ? null : (
              <span className="text-desktop-meta text-ds-slate-500">
                {identityLine}
              </span>
            )}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={ROW_BUTTON_CLASS}
            onClick={() => {
              // A fresh pick every time, so an abandoned removal is not
              // waiting on the page one Save away from deleting the photo.
              resetAvatarPicker();
              openDesktopSettings({ page: "avatar" });
            }}
          >
            {t("Change photo")}
          </Button>
        </div>
        {fields.map((row) =>
          row.field === editing ? (
            <FieldEditor
              key={row.field}
              field={row.field}
              title={row.title}
              value={row.value}
            />
          ) : (
            <SettingsRow
              key={row.field}
              leading={null}
              title={row.title}
              subtitle={row.value === "" ? row.placeholder : row.value}
              right={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={ROW_BUTTON_CLASS}
                  onClick={() => {
                    openDesktopSettings({
                      page: "account",
                      editing: row.field,
                    });
                  }}
                >
                  {t("Edit")}
                </Button>
              }
            />
          )
        )}
        <SettingsRow
          leading={null}
          title={t("Email")}
          subtitle={currentUser.email}
          right={null}
        />
      </SettingsSection>

      {/* Figma "section · suppressions": pinned to the bottom of the panel. */}
      <div className="mt-auto flex flex-col gap-1">
        <div className="h-px bg-ds-slate-100" />
        <DangerRow
          title={t("Delete all conversations")}
          subtitle={
            history === null
              ? null
              : history.total === 0
                ? t("Your JuisciGPT history is empty")
                : t("{{count}} conversations in your JuisciGPT history", {
                    count: history.total,
                  })
          }
          disabled={history === null || history.total === 0}
          onClick={() => {
            openDesktopSettings({ page: "deleteConversations" });
          }}
        />
        <DangerRow
          title={t("Delete account")}
          subtitle={null}
          disabled={false}
          onClick={() => {
            openDesktopSettings({ page: "deleteAccount" });
          }}
        />
      </div>
    </SettingsPage>
  );
}

type FieldForm = { value: string };

/** The account write for one edited row, `null` for a link that is not one. */
function patchOf(
  field: AccountField,
  text: string
):
  | { firstname: string }
  | { lastname: string }
  | { bio: string }
  | { link: string }
  | null {
  switch (field) {
    case "firstName":
      return { firstname: text };
    case "lastName":
      return { lastname: text };
    case "bio":
      return { bio: text };
    case "link": {
      const link = formatProfileLink(text);
      return link !== null && isValidProfileLink(link) ? { link } : null;
    }
    default:
      throw new Error(field satisfies never);
  }
}

/**
 * One Account row open for editing, in the row itself: the field, a line on
 * where it shows, then Cancel and Save. Never a dialog over the modal.
 */
function FieldEditor({
  field,
  title,
  value,
}: {
  field: AccountField;
  title: string;
  value: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { register, handleSubmit, formState, watch } = useForm<FieldForm>({
    values: { value },
  });
  const draft = watch("value").trim();
  // The names and the link cannot be empty: the API requires both names, and
  // takes a link only as a URL. An empty bio clears it.
  const canSave =
    formState.isDirty &&
    !formState.isSubmitting &&
    (field === "bio" || draft !== "");

  function close(): void {
    // Only from this row: a save that ends after the reader opened another
    // row or page leaves that one, and its draft, alone.
    leaveDesktopSettingsPage({ page: "account", editing: field }, ACCOUNT_PAGE);
  }

  async function onSave(data: FieldForm): Promise<void> {
    const json = patchOf(field, data.value.trim());
    if (json === null) {
      displayToast(t("Please provide a valid link."), "warning");
      return;
    }
    try {
      await apiClient.patch("/webapp/private/users/me", { json });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    await reloadCurrentUser();
    close();
    displayToast(t("Profile updated"), "success");
  }

  const helper =
    field === "firstName" || field === "lastName"
      ? t("Shown on your profile and on collections you share.")
      : t("Shown on your profile.");

  return (
    <form
      className="flex flex-col gap-2.5 py-2.5"
      onSubmit={handleSubmit(onSave)}
    >
      <span className="text-sm font-semibold text-ds-slate-950">{title}</span>
      {field === "bio" ? (
        <Textarea
          autoFocus
          rows={4}
          aria-label={title}
          {...register("value")}
        />
      ) : (
        <Input
          autoFocus
          aria-label={title}
          autoComplete={
            field === "firstName"
              ? "given-name"
              : field === "lastName"
                ? "family-name"
                : "url"
          }
          {...register("value")}
        />
      )}
      <p className="m-0 text-desktop-meta text-ds-slate-700">{helper}</p>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={ROW_BUTTON_CLASS}
          onClick={close}
        >
          {t("Cancel")}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          className={ROW_BUTTON_CLASS}
          disabled={!canSave}
        >
          {t("Save")}
        </Button>
      </div>
    </form>
  );
}

/** A red row of the deletions block: the trash, the labels, a chevron. */
function DangerRow({
  title,
  subtitle,
  disabled,
  onClick,
}: {
  title: string;
  subtitle: string | null;
  disabled: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        // -mx-3 + px-3: the content stays aligned with the rows above while
        // the hover background reaches beyond it.
        "group -mx-3 flex cursor-pointer items-center gap-3.5 rounded-lg border-none bg-transparent px-3 py-2.5 text-left hover:bg-ds-strawberry-50",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-ds-strawberry-50 text-ds-strawberry-800 group-hover:bg-ds-strawberry-100 group-disabled:group-hover:bg-ds-strawberry-50 [&_svg]:size-4">
        <IconTrash stroke={1.5} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold text-ds-strawberry-800">
          {title}
        </span>
        {subtitle !== null ? (
          <span className="text-desktop-meta text-ds-slate-700">
            {subtitle}
          </span>
        ) : null}
      </span>
      <IconChevronRight
        stroke={1.5}
        className="size-4 shrink-0 text-ds-slate-700"
      />
    </button>
  );
}

/**
 * The JuisciGPT history the reader would delete: the engine that answers new
 * questions lists it, as on the history page, and its bulk route deletes it.
 * `null` until the engine is known and the count has loaded.
 */
function useConversationHistory(): {
  engine: ConversationEngine;
  total: number;
  listedAt: string;
} | null {
  const engine = useConversationEngine();
  const countInput = {
    query: { limit: 1, offset: 0, sort: "newest" as const },
  };
  const legacyQuery = useApiQuery(
    "/webapp/private/conversations",
    engine === "legacy" ? countInput : false
  );
  const assistantQuery = useApiQuery(
    "/assistant/conversations",
    engine === "assistant" ? countInput : false
  );
  switch (engine) {
    case "pending":
      return null;
    case "legacy":
      return legacyQuery.state === "success"
        ? {
            engine,
            total: legacyQuery.body.meta.total,
            listedAt: legacyQuery.body.meta.listedAt,
          }
        : null;
    case "assistant":
      return assistantQuery.state === "success"
        ? {
            engine,
            total: assistantQuery.body.meta.total,
            listedAt: assistantQuery.body.meta.listedAt,
          }
        : null;
    default:
      throw new Error(engine satisfies never);
  }
}

/**
 * The confirmation of a history deletion (Figma D3, 4927:77278): a page of the
 * settings modal with its way back to Account, never a dialog over the modal.
 * Once done, Account shows again and the toast counts what went.
 */
export function DeleteConversationsSection(): React.JSX.Element {
  const { t } = useTranslation();
  const history = useConversationHistory();
  const deleteConversations = useDeleteConversations();
  // useForm only for a submit-in-flight flag, without owning `useState`.
  const { handleSubmit, formState } = useForm();

  function backToAccount(): void {
    openDesktopSettings(ACCOUNT_PAGE);
  }

  async function onDelete(): Promise<void> {
    if (history === null)
      throw new Error("should not happen: delete with no history loaded");
    const done = await deleteConversations(history.engine, {
      target: "all",
      q: null,
      except: [],
      // The count the reader confirmed: a conversation started since is kept.
      asOf: history.listedAt,
    });
    // Not `backToAccount`: the reader may have moved on meanwhile.
    if (done)
      leaveDesktopSettingsPage({ page: "deleteConversations" }, ACCOUNT_PAGE);
  }

  return (
    <SettingsPage back={{ label: t("Account"), onBack: backToAccount }}>
      <SettingsSection title={t("Delete all conversations")} description={null}>
        <div className="flex flex-col gap-3 pt-1.5">
          {history !== null ? (
            <p className="m-0 text-desktop-body-s text-ds-slate-900">
              {t(
                "This permanently deletes your {{count}} JuisciGPT conversations, on every device.",
                { count: history.total }
              )}
            </p>
          ) : null}
          <p className="m-0 text-desktop-body-s text-ds-slate-900">
            {t(
              "Your collections, saved articles and visual abstracts are kept."
            )}
          </p>
          <p className="m-0 text-sm text-ds-slate-500">
            {t("This action cannot be undone.")}
          </p>
          <form className="flex gap-2 pt-1" onSubmit={handleSubmit(onDelete)}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={ROW_BUTTON_CLASS}
              onClick={backToAccount}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              size="sm"
              className={ROW_BUTTON_CLASS}
              disabled={
                history === null ||
                history.total === 0 ||
                formState.isSubmitting
              }
            >
              {t("Delete all")}
            </Button>
          </form>
        </div>
      </SettingsSection>
    </SettingsPage>
  );
}

/**
 * The confirmation of an account deletion (Figma D3, 4927:77278): a page of
 * the settings modal with its way back to Account, never a dialog over the
 * modal.
 */
export function DeleteAccountSection({
  currentUser,
}: {
  currentUser: CurrentUser;
}): React.JSX.Element {
  const { t } = useTranslation();
  const logout = useLogOutUser();
  // useForm only for a submit-in-flight flag, without owning `useState`.
  const { handleSubmit, formState } = useForm();

  async function onDelete(): Promise<void> {
    try {
      await apiClient.delete("/webapp/private/users/me", {});
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    gatEventUserDeleteAccount(currentUser.uid);
    closeDesktopSettings();
    displayToast(t("Account deleted"), "success");
    await logout({ skipApiCalls: true });
  }

  function backToAccount(): void {
    openDesktopSettings(ACCOUNT_PAGE);
  }

  const consequences = [
    t("Your profile, clinical profile and settings will be removed."),
    t(
      "Your conversations, collections, saved articles and visual abstracts will be deleted."
    ),
    t("Links to collections you shared will stop working."),
  ];

  return (
    <SettingsPage back={{ label: t("Account"), onBack: backToAccount }}>
      <SettingsSection title={t("Delete account")} description={null}>
        <div className="flex flex-col gap-3 pt-1.5">
          <p className="m-0 text-desktop-body-s text-ds-slate-900">
            {t(
              "Before you delete your account, take a moment to understand what will happen to your data:"
            )}
          </p>
          <ul className="m-0 flex list-disc flex-col gap-2 pl-5 text-desktop-body-s text-ds-slate-900">
            {consequences.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="m-0 text-sm text-ds-slate-500">
            {t("Deleting your account can't be undone.")}
          </p>
          <form className="flex gap-2 pt-1" onSubmit={handleSubmit(onDelete)}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={ROW_BUTTON_CLASS}
              onClick={backToAccount}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              size="sm"
              className={ROW_BUTTON_CLASS}
              disabled={formState.isSubmitting}
            >
              {t("Delete account")}
            </Button>
          </form>
        </div>
      </SettingsSection>
    </SettingsPage>
  );
}
